/**
 * User data layer — now backed by the API.
 *
 * The signatures are unchanged from the localStorage build. That was the point
 * of writing them promise-based from the start: every caller already awaits,
 * so moving the persistence to a server is a change of body and not a change
 * of contract. No component was touched to make this work.
 *
 * WHAT CHANGED BEHIND THE INTERFACE
 *  - `password` is no longer part of a returned User. The old `User` type
 *    carried one because the mock had to compare it in the browser; the server
 *    hashes it and never sends it back, which is the whole reason to have one.
 *  - `authenticate` no longer compares anything here. It posts the credentials
 *    and the server decides, setting an httpOnly session cookie.
 *  - Role and status changes are enforced server-side as well as offered in
 *    the UI. A hidden button was never a permission.
 */

import type { Role, User } from '../types';
import { api } from './apiClient';

/** What the API returns for a user. Same shape minus the password. */
interface ApiUser {
  id: string;
  name: string;
  email: string;
  role: Role;
  status: User['status'];
  assignedModuleIds: string[];
  createdAt: string;
}

/**
 * The API user as the app's `User`.
 *
 * `password` is filled with an empty string rather than dropped: the type
 * still declares it, and every screen that reads it is a screen that should
 * show nothing. Removing it from the type is a separate, wider change.
 */
function toUser(row: ApiUser): User {
  return {
    id: row.id,
    name: row.name,
    email: row.email,
    role: row.role,
    status: row.status,
    assignedModuleIds: row.assignedModuleIds ?? [],
    createdAt: row.createdAt,
    password: '',
  };
}

export interface UserInput {
  name: string;
  email: string;
  password?: string;
  role: Role;
  assignedModuleIds?: string[];
}

export const userService = {
  async listUsers(): Promise<User[]> {
    const rows = await api.get<ApiUser[]>('/users');
    return rows.map(toUser);
  },

  async getUserById(id: string): Promise<User | null> {
    // No per-id endpoint: the list is admin-only and short, and a normal user
    // may only ever look up themselves, which `/auth/me` already answers.
    try {
      const me = await api.get<ApiUser>('/auth/me');
      if (me.id === id) return toUser(me);
    } catch {
      return null;
    }
    try {
      const rows = await api.get<ApiUser[]>('/users');
      return rows.map(toUser).find((user) => user.id === id) ?? null;
    } catch {
      return null;
    }
  },

  /** Signs in. The session arrives as an httpOnly cookie, not a token here. */
  async authenticate(email: string, password: string): Promise<User | null> {
    try {
      const result = await api.post<{ user: ApiUser }>('/auth/login', { email, password });
      return toUser(result.user);
    } catch {
      // The server answers the same way for a wrong password and an unknown
      // address, on purpose; the caller shows one message for both.
      return null;
    }
  },

  async logout(): Promise<void> {
    try {
      await api.post<void>('/auth/logout');
    } catch {
      // Already signed out, or the server is unreachable. The local session is
      // cleared by the caller either way.
    }
  },

  /** The signed-in user, or null. Used to restore a session on page load. */
  async currentUser(): Promise<User | null> {
    try {
      return toUser(await api.get<ApiUser>('/auth/me'));
    } catch {
      return null;
    }
  },

  async createUser(input: UserInput): Promise<User> {
    const row = await api.post<ApiUser>('/users', {
      name: input.name.trim(),
      email: input.email.trim(),
      password: input.password || 'changeme123',
      role: input.role,
      assignedModuleIds: input.assignedModuleIds ?? [],
    });
    return toUser(row);
  },

  async updateUser(id: string, updates: Partial<UserInput>): Promise<User> {
    const row = await api.put<ApiUser>(`/users/${id}`, {
      ...(updates.name !== undefined ? { name: updates.name.trim() } : {}),
      ...(updates.email !== undefined ? { email: updates.email.trim() } : {}),
      ...(updates.role !== undefined ? { role: updates.role } : {}),
      ...(updates.assignedModuleIds !== undefined
        ? { assignedModuleIds: updates.assignedModuleIds }
        : {}),
      // An empty string means "unchanged", not "blank password".
      ...(updates.password ? { password: updates.password } : {}),
    });
    return toUser(row);
  },

  async setUserStatus(id: string, status: User['status']): Promise<User> {
    return toUser(await api.put<ApiUser>(`/users/${id}`, { status }));
  },

  async deleteUser(id: string): Promise<void> {
    await api.delete<void>(`/users/${id}`);
  },

  /** Assign / reassign the full set of modules a user can access. */
  async setAssignedModules(id: string, moduleIds: string[]): Promise<User> {
    return toUser(await api.put<ApiUser>(`/users/${id}`, { assignedModuleIds: moduleIds }));
  },

  /** Drops a module from every user, used when the module is deleted. */
  async revokeModuleEverywhere(moduleId: string): Promise<void> {
    // The grant rows are removed by the database when the module goes
    // (ON DELETE CASCADE), so there is nothing left to do here. Kept so the
    // call site reads the same and needs no change.
  },
};
